import "@/index.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { useState, useEffect, createContext, useContext, useRef } from "react";

// Pages publiques
import HomePage from "./pages/HomePage";
import SimulationPage from "./pages/SimulationPage";
import ThankYouPage from "./pages/ThankYouPage";
import PricingPage from "./pages/PricingPage";
import AboutPage from "./pages/AboutPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";

// Pages dashboard (accès public)
import DashboardPage from "./pages/dashboard/DashboardPage";

// Layout
import Layout from "./components/Layout";

// Auth Context
export const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Auth Callback Component
function AuthCallback() {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasProcessed = useRef(false);
  
  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    const processAuth = async () => {
      try {
        // Get session_id from URL - could be in hash or query params
        const hash = window.location.hash || "";
        const search = window.location.search || "";
        const fullUrl = hash + search;
        
        // Try multiple patterns to extract session_id
        let sessionId = null;
        
        // Pattern 1: #session_id=xxx
        const hashMatch = hash.match(/[#&]?session_id=([^&]+)/);
        if (hashMatch) sessionId = hashMatch[1];
        
        // Pattern 2: ?session_id=xxx
        if (!sessionId) {
          const searchMatch = search.match(/[?&]session_id=([^&]+)/);
          if (searchMatch) sessionId = searchMatch[1];
        }
        
        // Pattern 3: Just look for session_id anywhere
        if (!sessionId) {
          const anyMatch = fullUrl.match(/session_id=([^&]+)/);
          if (anyMatch) sessionId = anyMatch[1];
        }
        
        console.log("Auth callback - URL:", window.location.href);
        console.log("Auth callback - Session ID found:", sessionId ? "Yes" : "No");
        
        if (!sessionId) {
          setError("Session invalide ou expirée. Veuillez vous reconnecter.");
          setLoading(false);
          return;
        }
        
        // Call backend to process Google session
        const response = await fetch(`${API}/api/auth/google-session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ session_id: sessionId })
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          console.error("Auth response error:", data);
          throw new Error(data.detail || "Échec de l'authentification");
        }
        
        // Store token in localStorage as backup
        if (data.session_token) {
          localStorage.setItem("session_token", data.session_token);
          console.log("Session token stored successfully");
        }
        
        // Small delay to ensure cookie is set
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Redirect to dashboard
        window.location.replace("/mon-compte");
        
      } catch (err) {
        console.error("Auth error:", err);
        setError(err.message || "Erreur d'authentification. Veuillez réessayer.");
        setLoading(false);
      }
    };
    
    processAuth();
  }, []);
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#064E3B] via-[#065F46] to-[#047857]">
        <div className="text-center bg-white rounded-2xl p-8 shadow-xl max-w-md mx-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Erreur d'authentification</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="space-y-3">
            <a 
              href="/connexion" 
              className="block w-full py-3 px-4 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors"
            >
              Réessayer la connexion
            </a>
            <a 
              href="/" 
              className="block w-full py-3 px-4 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
            >
              Retour à l'accueil
            </a>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#064E3B] via-[#065F46] to-[#047857]">
      <div className="text-center bg-white rounded-2xl p-8 shadow-xl">
        <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-700 font-medium">Connexion en cours...</p>
        <p className="text-gray-500 text-sm mt-2">Veuillez patienter</p>
      </div>
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading, checkAuth } = useAuth();
  const location = useLocation();
  
  useEffect(() => {
    if (!user && !loading) {
      checkAuth();
    }
  }, [user, loading, checkAuth]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-secondary">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Chargement...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/connexion" state={{ from: location }} replace />;
  }
  
  return children;
}

// App Router with Auth Detection
function AppRouter() {
  const location = useLocation();
  
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check for session_id in URL hash (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public routes */}
      <Route element={<Layout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/simulation" element={<SimulationPage />} />
        <Route path="/merci" element={<ThankYouPage />} />
        <Route path="/tarifs" element={<PricingPage />} />
        <Route path="/a-propos" element={<AboutPage />} />
        <Route path="/paiement/success" element={<PaymentSuccessPage />} />
      </Route>
      
      {/* Auth routes */}
      <Route path="/connexion" element={<LoginPage />} />
      <Route path="/inscription" element={<RegisterPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Dashboard (accès sécurisé) */}
      <Route path="/mon-compte" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Auth Provider
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const checkAuth = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const headers = { "Content-Type": "application/json" };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API}/api/auth/me`, {
        credentials: "include",
        headers
      });
      
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        setUser(null);
        localStorage.removeItem("session_token");
      }
    } catch (error) {
      console.error("Auth check error:", error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };
  
  const login = async (email, password) => {
    const response = await fetch(`${API}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, password })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || "Erreur de connexion");
    }
    
    const data = await response.json();
    if (data.session_token) {
      localStorage.setItem("session_token", data.session_token);
    }
    setUser(data.user);
    return data;
  };
  
  const register = async (userData) => {
    const response = await fetch(`${API}/api/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(userData)
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || "Erreur d'inscription");
    }
    
    const data = await response.json();
    if (data.session_token) {
      localStorage.setItem("session_token", data.session_token);
    }
    setUser(data.user);
    return data;
  };
  
  const logout = async () => {
    try {
      await fetch(`${API}/api/auth/logout`, {
        method: "POST",
        credentials: "include"
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem("session_token");
    setUser(null);
  };
  
  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Redirect to /auth/callback which will process the session_id and then redirect to /mon-compte
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  
  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);
  
  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, loginWithGoogle, checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

function App() {
  return (
    <div className="App min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
